import * as React from "react";
import { Box, Button, Card, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/ssr/ArrowRight";
import { CheckIcon } from "@phosphor-icons/react/dist/ssr/Check";

import type { CampaignRequestDetail } from "@/hooks/api/use-campaign-requests";

import { languageLabel } from "../constants";
import { isDefaultAgentName } from "../view/agent-naming";
import { LAST_STEP, WIZARD_STEPS } from "./steps";

/**
 * The furniture every step sits in: the rail on the left, the heading above the fields and the
 * footer under them.
 *
 * Each step owns its own footer rather than the shell owning one, because each step saves through
 * its own endpoint before it advances — the button and the write that has to happen behind it
 * belong to the same component.
 */

export interface StepProps {
	request: CampaignRequestDetail;
	/** Move to another step. Steps call this only after their own save has resolved. */
	goTo: (step: number) => void;
	/** Leave the wizard. The draft is already persisted, so this loses nothing. */
	exit: () => void;
	/**
	 * The commercial terms are settled and nothing on this step may be written.
	 *
	 * Every write behind the wizard is refused by the API once a request is `confirmed` or
	 * beyond — `CampaignRequestPolicy.updateSettled` is ours alone — so a step that stays
	 * editable is offering the client edits it will then throw away with a 422 on Continue.
	 * A banner saying so is not enough: they have already retyped the field by the time they
	 * read it. Steps disable their inputs against this and navigate rather than save.
	 */
	readOnly: boolean;
}

export interface WizardRailProps {
	current: number;
	/** Free navigation is only offered once there is something to come back to. */
	canNavigate: boolean;
	/**
	 * Which steps the request actually holds the work for, by index — see `../progress`.
	 *
	 * Passed in rather than worked out here because it is not the rail's question: the same
	 * answer decides where a draft row on the list reopens, and a rail that computed its own
	 * would be free to disagree with it.
	 */
	completed: boolean[];
	onSelect: (step: number) => void;
}

/**
 * Where they are and what is left. Completed steps carry a tick rather than their number, which is
 * the only affordance in the wizard that says "this is saved" — so it has to be true.
 *
 * It was not. The tick used to be `step.index < current`, which is a statement about the cursor
 * and not about the request: opening `?step=6` on an untouched draft drew ticks against five steps
 * nobody had filled in, and the client's only reason to trust that their work was saved was a mark
 * the wizard put there for walking past.
 */
export function WizardRail({ current, canNavigate, completed, onSelect }: WizardRailProps): React.JSX.Element {
	return (
		<Card variant="outlined" sx={{ p: 2, position: { md: "sticky" }, top: { md: 24 } }}>
			<Stack spacing={0.5}>
				{WIZARD_STEPS.map((step) => {
					const isCurrent = step.index === current;
					// The step being looked at keeps its number even when it is finished: the tick is
					// about what is behind them, and the current step is the one still being worked on.
					const isDone = !isCurrent && completed[step.index] === true;

					return (
						<Box
							key={step.key}
							component="button"
							type="button"
							disabled={!canNavigate}
							onClick={() => onSelect(step.index)}
							sx={{
								alignItems: "center",
								background: isCurrent ? "var(--mui-palette-action-selected)" : "transparent",
								border: "none",
								borderRadius: 1,
								cursor: canNavigate ? "pointer" : "default",
								display: "flex",
								gap: 1.5,
								px: 1.5,
								py: 1,
								textAlign: "left",
								width: "100%",
								"&:hover": canNavigate ? { background: "var(--mui-palette-action-hover)" } : undefined,
							}}
						>
							<Box
								sx={{
									alignItems: "center",
									bgcolor: isDone || isCurrent ? "primary.main" : "transparent",
									border: "1px solid var(--mui-palette-divider)",
									borderColor: isDone || isCurrent ? "primary.main" : "divider",
									borderRadius: "50%",
									color: isDone || isCurrent ? "primary.contrastText" : "text.secondary",
									display: "flex",
									flexShrink: 0,
									fontSize: 12,
									height: 24,
									justifyContent: "center",
									width: 24,
								}}
							>
								{isDone ? <CheckIcon size={14} weight="bold" /> : step.index}
							</Box>

							<Typography
								variant="body2"
								color={isCurrent ? "text.primary" : "text.secondary"}
								sx={{ fontWeight: isCurrent ? 600 : 400 }}
							>
								{step.label}
							</Typography>
						</Box>
					);
				})}
			</Stack>
		</Card>
	);
}

export interface StepHeadingProps {
	title: string;
	/** The one line under the heading. Every step in the canvas has one; none of them is optional. */
	help?: React.ReactNode;
	children?: React.ReactNode;
}

export function StepHeading({ title, help, children }: StepHeadingProps): React.JSX.Element {
	return (
		<Stack spacing={0.5}>
			<Typography variant="h5">{title}</Typography>
			{help ? (
				<Typography variant="body2" color="text.secondary">
					{help}
				</Typography>
			) : null}
			{children}
		</Stack>
	);
}

export interface WizardFooterProps {
	onBack?: () => void;
	onSaveExit?: () => void;
	onContinue: () => void;
	continueLabel?: string;
	continueDisabled?: boolean;
	busy?: boolean;
	/** The equal-weight alternative a step offers: "I'll send leads later", "Skip — use defaults". */
	secondaryLabel?: string;
	onSecondary?: () => void;
	/** Anything the step wants to state at the moment of committing — the running setup total. */
	leading?: React.ReactNode;
}

export function WizardFooter({
	onBack,
	onSaveExit,
	onContinue,
	continueLabel = "Continue",
	continueDisabled = false,
	busy = false,
	secondaryLabel,
	onSecondary,
	leading,
}: WizardFooterProps): React.JSX.Element {
	return (
		<Stack spacing={2}>
			<Divider />

			<Stack
				direction={{ xs: "column", sm: "row" }}
				spacing={2}
				sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
			>
				<Box>
					{onBack ? (
						<Button color="inherit" startIcon={<ArrowLeftIcon />} disabled={busy} onClick={onBack}>
							Back
						</Button>
					) : null}
				</Box>

				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1.5}
					sx={{ alignItems: { sm: "center" }, flexWrap: "wrap" }}
				>
					{leading}

					{secondaryLabel && onSecondary ? (
						<Button color="inherit" disabled={busy} onClick={onSecondary}>
							{secondaryLabel}
						</Button>
					) : null}

					{onSaveExit ? (
						<Button color="inherit" variant="outlined" disabled={busy} onClick={onSaveExit}>
							Save &amp; exit
						</Button>
					) : null}

					<Button
						variant="contained"
						endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArrowRightIcon />}
						disabled={busy || continueDisabled}
						onClick={onContinue}
					>
						{continueLabel}
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
}

/**
 * How an agent is named wherever it is shown beside its market: "English", or "English · Poly EN".
 *
 * Step 2 creates an agent with the language as its nickname, which is the right default and the
 * reason almost every campaign carried a badge reading "English · English". A pill that repeats
 * itself makes the client look twice for a difference that is not there, so the name is only
 * added once it is actually a name — and where it is added, the language leads, because the
 * language is the thing the market is being told.
 */
export function agentBadge(agentLabel: string | null | undefined, language: string): string {
	const spoken = languageLabel(language);

	// Two defaults, not one: the bare language, and `Q4 Renewals — English` on a titled campaign.
	// Asking `isDefaultAgentName` rather than comparing here is what keeps this pill and the one
	// on the request page answering the same question.
	if (isDefaultAgentName(agentLabel, language)) return spoken;
	return `${spoken} · ${(agentLabel ?? "").trim()}`;
}

/**
 * "Step 3 of 8" — the only progress number the client is given, and it never counts step 0.
 *
 * Empty on step 0, because the heading beside it already reads "New request" and a counter that
 * repeats its neighbour word for word is the second copy of a fact the screen has already given.
 */
export function stepCounter(step: number): string {
	if (step <= 0) return "";
	return `Step ${step} of ${LAST_STEP}`;
}

/**
 * Whether the step on screen is holding edits it has not written yet.
 *
 * Every step saves on Continue and on Save & exit, and only there. The rail is a third way out
 * that saves nothing — click "Compliance" half way through retyping a schedule and the schedule
 * is gone, with no prompt and nothing to undo it with. That is the one navigation in the wizard
 * that can lose work, so it is the one that has to ask.
 *
 * A single boolean rather than a map by step, because exactly one step is mounted at a time and
 * the answer only ever concerns that one. The step reports through {@link useWizardDirty}, which
 * clears on unmount — so a step that navigates after saving cannot leave a stale claim behind.
 */
const WizardDirtyContext = React.createContext<((dirty: boolean) => void) | null>(null);

export const WizardDirtyProvider = WizardDirtyContext.Provider;

/**
 * Tell the shell whether this step has unsaved edits. Call it with whatever the step already
 * uses to decide if it needs to write at all — react-hook-form's `isDirty`, or the same
 * comparison its `save()` skips on. The two answers must be the same answer.
 */
/**
 * For a step that navigates itself after a write — the confirm on step 8.
 *
 * `useWizardDirty` clears on unmount, and the blocker runs *before* the unmount, so a step that
 * calls `navigate()` while its own comparison still reads dirty gets its own "discard changes?"
 * dialog over work that was just saved. Call this first, then navigate.
 */
export function useWizardLeaving(): () => void {
	const report = React.useContext(WizardDirtyContext);
	return React.useCallback(() => report?.(false), [report]);
}

export function useWizardDirty(dirty: boolean): void {
	const report = React.useContext(WizardDirtyContext);

	React.useEffect(() => {
		if (!report) return;

		report(dirty);
		// Unmounting is either a save that has already landed or a discard the client agreed to;
		// either way the next step starts clean rather than inheriting this one's answer.
		return () => report(false);
	}, [dirty, report]);
}
